import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { PolygonTypes } from '../../../helpers/constants';
import {
  getIntersectionPoint,
  getParellelPoints,
  getParellelPoints2,
  nauticalMileToMeter,
} from '../../../utils/math.util';
import HookablePoint from '../HookablePoint';
import useMapLocation from '../hooks/useMapLocation';
import MapSizeContext from '../MapSizeContext';
import { Circle } from './Circle';
import { CircularSector } from './CircularSector';
import DrawPolygon from './Polygon';
import { PolyLine } from './PolyLine';

export interface IPolygonProps {
  monitor: string;
  polygon: any;
  type: string;
}

const transformation = {
  [PolygonTypes.CIRCLE]: {
    transform: (polygon: any, latLangToXY: any, distanceToPixel: any) => {
      const { center, radius } = polygon;
      const latitude = `${center.latV} ${center.latH}`;
      const longitude = `${center.longV} ${center.longH}`;
      const [translatedX, translatedY] = latLangToXY(longitude, latitude);
      const radiusInMeters = nauticalMileToMeter(radius);
      return {
        x: translatedX,
        y: translatedY,
        radius: distanceToPixel(radiusInMeters),
        fillingColor: polygon.fillingColor,
        lineColor: polygon.lineColor,
        lineType: polygon.lineType,
        arn: polygon.arn,
        latitude,
        longitude,
        hookProperties: polygon.hookProperties,
        labelFontSize: polygon.labelFontSize,
      };
    },
    Element: Circle,
  },
  [PolygonTypes.CIRCULAR_SECTOR]: {
    transform: (polygon: any, latLangToXY: any, distanceToPixel: any) => {
      const {
        center,
        sectorBearings,
        arn,
        innerCircleRadius,
        innerCircleLineType,
        innerCircleLineColor,
        outerCircleRadius,
        outerCircleLineType,
        outerCircleLineColor,
        hookProperties,
        centerVisible,
        largeArcFlag,
        sweepFlag,
      } = polygon;
      const latitude = `${center.latV} ${center.latH}`;
      const longitude = `${center.longV} ${center.longH}`;
      const [translatedX, translatedY] = latLangToXY(longitude, latitude);

      return {
        x: translatedX,
        y: translatedY,
        sectorBearings,
        arn,
        innerCircleRadius: distanceToPixel(
          nauticalMileToMeter(innerCircleRadius)
        ),
        outerCircleRadius: distanceToPixel(
          nauticalMileToMeter(outerCircleRadius)
        ),
        innerCircleLineType,
        innerCircleLineColor,
        outerCircleLineType,
        outerCircleLineColor,
        latitude,
        longitude,
        hookProperties,
        centerVisible,
        largeArcFlag: largeArcFlag || 0,
        sweepFlag: sweepFlag || 0,
      };
    },
    Element: CircularSector,
  },
  [PolygonTypes.POLYLINE]: {
    transform: (polygon: any, latLangToXY: any, distanceToPixel: any) => {
      const {
        seqOfPoints,
        showChannelPath,
        channelWidth,
        showPoints,
        labelFontSize,
      } = polygon;
      const xySeqPoints = seqOfPoints
        .map((point: any) => ({
          latitude: `${point.latV} ${point.latH}`,
          longitude: `${point.longV} ${point.longH}`,
          pointLabel: point.pointLabel,
        }))
        .map((point: any) => {
          const [x, y] = latLangToXY(point.longitude, point.latitude);
          return {
            x,
            y,
            pointLabel: point.pointLabel,
            latitude: point.latitude,
            longitude: point.longitude,
          };
        });
      return {
        points: xySeqPoints,
        lineColor: polygon.lineColor,
        lineWidth: polygon.lineWidth,
        lineType: polygon.lineType,
        showDirectionalArrow: polygon.showDirectionalArrow,
        showPoints,
        labelFontSize,
        rn: polygon.rn,
        showChannelPath,
        channelWidth: distanceToPixel(nauticalMileToMeter(channelWidth || 2)),
        hookProperties: polygon.hookProperties,
      };
    },
    Element: PolyLine,
  },
  [PolygonTypes.POLYGON]: {
    transform: (polygon: any, latLangToXY: any, distanceToPixel: any) => {
      const { seqOfPoints } = polygon;
      const xySeqPoints = seqOfPoints
        .map((point: any) => ({
          latitude: `${point.latV} ${point.latH}`,
          longitude: `${point.longV} ${point.longH}`,
          pointLabel: point.pointLabel,
        }))
        .map((point: any) => {
          const [x, y] = latLangToXY(point.longitude, point.latitude);
          return {
            x,
            y,
            pointLabel: point.pointLabel,
            latitude: point.latitude,
            longitude: point.longitude,
          };
        });
      return {
        points: xySeqPoints,
        lineColor: polygon.lineColor,
        lineWidth: polygon.lineWidth,
        lineType: polygon.lineType,
        showDirectionalArrow: polygon.showDirectionalArrow,
        fillingColor: polygon.fillingColor,
        arn: polygon.arn,
        hookProperties: polygon.hookProperties,
        labelFontSize: polygon.labelFontSize,
      };
    },
    Element: DrawPolygon,
  },
};

export default function Polygon(props: IPolygonProps) {
  console.log('polygon', { props });
  const { monitor, polygon, type } = props;
  const { width, height } = useContext(MapSizeContext);
  const { latLangToXY, rerender, distanceToPixel } = useMapLocation(monitor, {
    width,
    height,
  });
  const [polygonProps, setPolygonProps] = useState<any>(null);
  useEffect(() => {
    if (polygon) {
      const { transform } = transformation[type];
      const result = transform(polygon, latLangToXY, distanceToPixel);
      setPolygonProps(result);
    }
  }, [rerender, polygon]);
  const { Element } = transformation[type];
  return polygonProps ? <Element {...polygonProps} /> : null;
}
